// The item pieces the Job view and other screens share (the item panel itself is screens/job/walk/ItemPanel.tsx since
// the Job view rebuild, 2026-10-22): one value edited the way its type calls for, an input line with its hold tick,
// the library pickers (MaterialPick, RecipePick, CatalogPick, OperationPick), a custom item's own route and BOM, and
// several items edited at once. A derived or linked value is gray and locked; it is edited at its source, or held on
// the item with the checkbox beside it.

import { useState } from 'react';
import { type MaterialFilter, type ResolvedInput, type ResolvedItem, type Resolution, type Scalar } from '@zerogap/engine';
import { updateRow, type Row } from '../../lib/api';
import { inches, minutes, qty, readLength } from '../../lib/format';
import { ensureStored } from '../../lib/job';
import { useSession } from '../../lib/session';
import { Btn, Edit, Empty, Info, Ln, Money, Panel } from '../../ui/kit';
import { priceWords } from '../../ui/pack';
import { catalogFits, filterWords, materialFits } from '../../ui/materialfilter';
import { Pick } from '../../ui/pick';
import type { JobCtx } from './Workspace';

export function showValue(v: Scalar, type: string, unit: string | null): string {
  if (v === null || v === undefined || v === '') return '—';
  if (type === 'boolean') return v === true || v === 'true' ? 'yes' : 'no';
  if (type === 'number') return unit === 'in' ? inches(v) : qty(v as string);
  return String(v);
}

/** One value, edited the way its type calls for. Saving '' clears it. A choice named in `unavailable` is grayed, with its rule beside it. */
export function ValueEditor({ type, unit, choices, value, onSave, disabled, unavailable }: { type: string; unit: string | null; choices: string[] | null; value: Scalar; onSave: (v: Scalar) => void; disabled?: boolean; unavailable?: Record<string, string> }) {
  if (type === 'boolean') {
    return <input type="checkbox" disabled={disabled} checked={value === true || value === 'true'} onChange={(e) => onSave(e.target.checked)} />;
  }
  if (type === 'choice' && choices) {
    return (
      <select disabled={disabled} value={value === null ? '' : String(value)} onChange={(e) => onSave(e.target.value === '' ? null : e.target.value)}>
        <option value="">—</option>
        {choices.map((c) => (
          <option key={c} value={c} disabled={!!unavailable?.[c]}>
            {c}
            {unavailable?.[c] ? ` · not available (${unavailable[c]})` : ''}
          </option>
        ))}
      </select>
    );
  }
  if (type === 'number') {
    const shown = value === null || value === '' ? '' : unit === 'in' ? inches(value).replace(/"$/, '') : qty(value as string);
    return (
      <Edit
        className="short right"
        disabled={disabled}
        value={shown}
        placeholder={unit === 'in' ? '24 3/8' : ''}
        parse={(t) => (t.trim() === '' ? '' : readLength(t) ?? undefined)}
        onSave={(v) => onSave(v === '' ? null : v)}
      />
    );
  }
  return <Edit disabled={disabled} value={value === null ? '' : String(value)} onSave={(v) => onSave(v === '' ? null : v)} />;
}

/** One input or held value on an item, written to its row (a child the engine created gets a row first). `all` is the resolution the item came from, when it is not the job as priced (an add-on item on the walk). */
export async function writeItem(ctx: JobCtx, item: ResolvedItem, field: 'inputs' | 'overrides', key: string, value: Scalar, all?: ResolvedItem[]) {
  const id = await ensureStored(item, ctx.jobId, all ?? ctx.r.res!.items);
  const row = ctx.data.items.find((i) => i.id === id);
  const next: Record<string, Scalar> = { ...((row?.[field] as Record<string, Scalar>) ?? {}) };
  if (value === null || value === '') delete next[key];
  else next[key] = value;
  await updateRow('job_item', id, { [field]: next });
}


/** A value shown as words: a material or catalog item by name, anything else as typed. */
export function wordsFor(lib: NonNullable<JobCtx['r']['lib']>, type: string, value: Scalar, unit: string | null): string {
  if (type === 'material') return lib.materials[String(value ?? '')]?.description ?? '—';
  if (type === 'catalog_item') return lib.catalog[String(value ?? '')]?.name ?? '—';
  return showValue(value, type, unit);
}

/** The editor for one value of any type a standard or input can have, including a library pick. */
export function AnyEditor({ lib, type, unit, choices, value, filter, disabled, onSave }: { lib: NonNullable<JobCtx['r']['lib']>; type: string; unit: string | null; choices: string[] | null; value: Scalar; filter?: MaterialFilter | null; disabled: boolean; onSave: (v: Scalar) => void }) {
  if (type === 'material') {
    return (
      <span className="nowrap">
        <MaterialPick lib={lib} value={String(value ?? '')} filter={filter} onPick={(id) => onSave(id)} />
        {value && !disabled && (
          <Btn small title="Clear" onClick={() => onSave(null)}>
            ×
          </Btn>
        )}
      </span>
    );
  }
  if (type === 'catalog_item') return <CatalogPick lib={lib} value={String(value ?? '')} filter={filter} onPick={(id) => onSave(id)} />;
  if (type === 'recipe') return <RecipePick lib={lib} value={String(value ?? '')} onPick={(name) => onSave(name)} />;
  return <ValueEditor type={type} unit={unit} choices={choices} value={value} disabled={disabled} onSave={onSave} />;
}

/**
 * A type choice (Door type A, Hardware type A, Box finish Type A) names a job standard by its letter: door_type_a,
 * hardware_a, finish_a. What that standard holds today, in words, so it reads in the inputs area without opening
 * Job setup (Spencer, 2026-10-15). The item's own held value where it has one; the job's otherwise.
 */
function typeResolves(ctx: JobCtx, item: ResolvedItem, input: ResolvedInput): string | null {
  const lib = ctx.r.lib!;
  const res = ctx.r.res!;
  if (input.data_type !== 'choice') return null;
  const m = /^type\s+([a-z])$/i.exec(String(input.value ?? '').trim());
  if (!m) return null;
  const letter = m[1]!.toLowerCase();
  const stem = input.key.replace(/_type$/, '');
  const last = input.key.split('_').pop() ?? input.key;
  for (const key of new Set([`${input.key}_${letter}`, `${stem}_${letter}`, `${last}_${letter}`])) {
    const def = lib.standards.find((d) => d.key === key);
    if (!def) continue;
    const use = item.standards_used.find((s) => s.key === key);
    const value = use ? use.value : (res.standards[key] ?? null);
    const text =
      value === null || value === ''
        ? null
        : def.data_type === 'material'
          ? (lib.materials[String(value)]?.description ?? null)
          : def.data_type === 'catalog_item'
            ? (lib.catalog[String(value)]?.name ?? null)
            : String(value);
    return `${def.label}: ${text ?? 'not set on this job'}${use?.overridden ? ' · held on this item' : ''}`;
  }
  return null;
}

export function InputLine({ ctx, item, input, canEdit, all, lit }: { ctx: JobCtx; item: ResolvedItem; input: ResolvedInput; canEdit: boolean; all?: ResolvedItem[]; lit?: boolean }) {
  const lib = ctx.r.lib!;
  // hidden by its rule: it keeps its value and is not shown (Spencer, 2026-10-07)
  if (input.hidden) return null;
  // a type choice says what it stands for on this job, under the choice (Spencer, 2026-10-15)
  const resolves = typeResolves(ctx, item, input);
  const under = resolves ? <small className="muted resolves">{resolves}</small> : null;
  const overridden = input.source === 'override';
  const lockedSource = input.source === 'parent' || input.source === 'standard' || overridden || (!!input.standard_key && input.source === 'blank');
  const follows = input.standard_key ? lib.standards.find((d) => d.key === input.standard_key) : undefined;
  const noOverride = input.can_override === false;
  const why = input.source === 'parent'
    ? 'Worked out from its parent. Edit the parent, or tick the box to hold a value here.'
    : follows
      ? `Follows the ${follows.kind === 'company' ? 'company' : 'job'} standard ${follows.label}. Change it in Job setup${noOverride ? '; no item may hold its own value of it.' : ', or tick the box to hold a value on this item.'}`
      : 'Filled by a job standard. Change it in Job setup, or tick the box to hold a value on this item.';
  if (lockedSource) {
    return (
      <Ln
        className={lit ? 'lit' : undefined}
        left={
          noOverride ? (
            <>
              {input.label}
              <Info>{why}</Info>
              {input.info && <Info>{input.info}</Info>}
            </>
          ) : (
            <label className="check">
              <input
                type="checkbox"
                disabled={!canEdit}
                checked={overridden}
                title="Override on this item"
                onChange={(e) => void writeItem(ctx, item, 'overrides', input.key, e.target.checked ? input.value : null, all)}
              />
              {input.label}
              {!overridden && <Info>{why}</Info>}
              {input.info && <Info>{input.info}</Info>}
            </label>
          )
        }
        right={
          overridden ? (
            <span className="ov">
              <AnyEditor lib={lib} type={input.data_type} unit={input.unit} choices={input.choices} value={input.value} filter={follows?.material_filter ?? input.material_filter} disabled={!canEdit} onSave={(v) => void writeItem(ctx, item, 'overrides', input.key, v, all)} />
              {input.unit && input.data_type === 'number' && <small> {input.unit}</small>}
            </span>
          ) : (
            <span className="lk">{wordsFor(lib, input.data_type, input.value, input.unit)}</span>
          )
        }
        under={under}
      />
    );
  }
  return (
    <Ln
      className={lit ? 'lit' : undefined}
      left={
        <>
          {input.label}
          {input.source === 'default' && <small> · default</small>}
          {input.data_type === 'material' && filterWords(input.material_filter) && <small> · {filterWords(input.material_filter)}</small>}
          {input.info && <Info>{input.info}</Info>}
        </>
      }
      right={
        <span className="nowrap">
          <AnyEditor lib={lib} type={input.data_type} unit={input.unit} choices={input.choices} value={input.value} filter={input.material_filter} disabled={!canEdit} onSave={(v) => void writeItem(ctx, item, 'inputs', input.key, v, all)} />
          {input.unit && input.data_type === 'number' && <small> {input.unit}</small>}
        </span>
      }
      under={under}
    />
  );
}

// ---------- custom item: route and BOM edited in place ----------

/**
 * The pickers on a job: one shared control (ui/pick.tsx), a search box over the options under their headings,
 * because a dropdown of three hundred materials could not be typed into (Spencer, 2026-10-16). Each keeps the
 * choice already made on its list, whatever the filter.
 */

/** A material from the library, under its category and subcategory. A filter may limit the list to materials carrying a key, or to one category and subcategory (Hardware A: pulls and knobs). */
export function MaterialPick({ lib, value, onPick, filter }: { lib: NonNullable<JobCtx['r']['lib']>; value: string; onPick: (id: string) => void; filter?: MaterialFilter | null }) {
  const mats = Object.values(lib.materials)
    .filter((m) => m.id === value || materialFits(m, filter))
    .sort((a, b) => (a.category ?? '').localeCompare(b.category ?? '') || (a.subcategory ?? '').localeCompare(b.subcategory ?? '') || a.description.localeCompare(b.description));
  const words = filterWords(filter);
  return (
    <Pick
      value={value}
      onPick={onPick}
      style={{ maxWidth: 280 }}
      title={words ? `Only ${words}` : undefined}
      placeholder={words ? `Pick ${(filter?.key ?? filter?.subcategory ?? filter?.category ?? 'a material').toLowerCase()}…` : 'Pick a material…'}
      options={mats.map((m) => ({
        id: m.id,
        label: m.description,
        group: [m.category, m.subcategory].filter(Boolean).join(' · ') || 'No category',
        detail: priceWords(m),
      }))}
    />
  );
}

/** A recipe name: every name the catalog items give their recipes, the most used first, each with how many items carry it. */
export function RecipePick({ lib, value, onPick }: { lib: NonNullable<JobCtx['r']['lib']>; value: string; onPick: (name: string) => void }) {
  const count = new Map<string, { name: string; items: number }>();
  for (const c of Object.values(lib.catalog)) {
    for (const name of new Set(c.recipes.map((r) => r.name.trim()).filter(Boolean))) {
      const k = name.toLowerCase();
      const cur = count.get(k);
      if (cur) cur.items += 1;
      else count.set(k, { name, items: 1 });
    }
  }
  const names = [...count.values()].sort((a, b) => b.items - a.items || a.name.localeCompare(b.name));
  // the stored value must match an option exactly to show; a spelling no item uses is said so
  const stray = value && !names.some((n) => n.name === value) ? value : null;
  const known = !!stray && count.has(stray.trim().toLowerCase());
  return (
    <Pick
      value={value}
      onPick={onPick}
      style={{ maxWidth: 280 }}
      placeholder="Pick a recipe…"
      options={[
        ...names.map((n) => ({ id: n.name, label: n.name, detail: `${n.items} item${n.items === 1 ? '' : 's'}` })),
        ...(stray ? [{ id: stray, label: stray, detail: known ? null : 'no item has a recipe of this name' }] : []),
      ]}
    />
  );
}

export function CatalogPick({ lib, value, onPick, filter }: { lib: NonNullable<JobCtx['r']['lib']>; value: string; onPick: (id: string) => void; filter?: MaterialFilter | null }) {
  // a retired item is offered to no new job; a filter keeps to one family; the one already chosen stays on the list
  const items = Object.values(lib.catalog).filter((c) => (!c.retired && catalogFits(c, filter)) || c.id === value).sort((a, b) => (a.family ?? '').localeCompare(b.family ?? '') || a.name.localeCompare(b.name));
  // under the family when the picker offers them all; the names alone when it keeps to one family (Spencer, 2026-10-14)
  return <Pick value={value} onPick={onPick} style={{ maxWidth: 280 }} placeholder="Pick a catalog item…" options={items.map((c) => ({ id: c.id, label: c.name, group: filter?.family ? null : c.family || 'No family' }))} />;
}

export function OperationPick({ lib, value, onPick }: { lib: NonNullable<JobCtx['r']['lib']>; value: string; onPick: (id: string) => void }) {
  const ops = Object.values(lib.operations).filter((o) => !o.retired || o.id === value).sort((a, b) => (a.category ?? '').localeCompare(b.category ?? '') || a.name.localeCompare(b.name));
  return (
    <Pick
      value={value}
      onPick={onPick}
      style={{ maxWidth: 280 }}
      placeholder="Pick an operation…"
      options={ops.map((o) => ({ id: o.id, label: o.name, group: o.category || 'No category', detail: `${o.minutes_per_unit ? `${qty(o.minutes_per_unit)} min` : 'no standard'} ${o.unit ?? ''}`.trim() }))}
    />
  );
}

export function CustomLines({ ctx, item, canEdit, res }: { ctx: JobCtx; item: ResolvedItem; canEdit: boolean; res?: Resolution }) {
  const lib = ctx.r.lib!;
  const row = ctx.data.items.find((i) => i.id === item.id);
  const group = (res ?? ctx.r.res!).groups.find((g) => g.key === item.group_key)!;
  const route = ((row?.custom_route as Row[]) ?? []).slice();
  const bom = ((row?.custom_bom as Row[]) ?? []).slice();
  const [op, setOp] = useState('');
  const [mat, setMat] = useState('');
  const save = (p: Row) => updateRow('job_item', item.id, p);

  return (
    <>
      <Panel label="Route · pick an operation, type how much">
        {route.length === 0 && <Empty>No operations yet.</Empty>}
        {route.map((line, i) => {
          const o = lib.operations[line.operation_id];
          const st = group.steps[i];
          return (
            <Ln
              key={i}
              left={
                <>
                  {o?.name ?? '(operation removed)'}
                  <small>
                    {' '}
                    · {o?.labor_class_id ? lib.laborClasses[o.labor_class_id]?.name : 'no class'} · {o?.minutes_per_unit ? `${qty(o.minutes_per_unit)} min ${o.unit ?? ''}` : 'no time standard'}
                    {o?.setup_minutes ? ` + ${qty(o.setup_minutes)} setup` : ''}
                  </small>
                  {st?.warnings.map((w) => (
                    <div key={w} className="warn">
                      {w}
                    </div>
                  ))}
                </>
              }
              right={
                <span className="nowrap">
                  <Edit
                    className="short right"
                    disabled={!canEdit}
                    value={line.qty ?? ''}
                    parse={(t) => (t.trim() === '' ? '' : readLength(t) ?? undefined)}
                    onSave={(v) => save({ custom_route: route.map((l, k) => (k === i ? { ...l, qty: v === '' ? 0 : v } : l)) })}
                  />{' '}
                  → {minutes(st?.minutes)}{' '}
                  {canEdit && (
                    <Btn small title="Remove this step" onClick={() => window.confirm(`Remove "${o?.name ?? 'this operation'}" from this item's route?`) && void save({ custom_route: route.filter((_, k) => k !== i) })}>
                      ×
                    </Btn>
                  )}
                </span>
              }
            />
          );
        })}
        {canEdit && (
          <div className="row mid" style={{ marginTop: 8, gap: 6 }}>
            <OperationPick lib={lib} value={op} onPick={setOp} />
            <Btn
              small
              disabled={!op}
              onClick={async () => {
                await save({ custom_route: [...route, { operation_id: op, qty: 1 }] });
                setOp('');
              }}
            >
              + Operation
            </Btn>
          </div>
        )}
      </Panel>
      <Panel
        label="BOM · pick a material, or type a line, and how much"
        right={<Info left>A typed line is a custom line: what it is, how many, the cost of one as quoted, the vendor, the date of the quote and its link. It is priced like a material (the materials markup and the margin). An AI employee may suggest one; it lands here when you approve it.</Info>}
      >
        {bom.length === 0 && <Empty>No materials yet.</Empty>}
        {bom.map((line, i) => {
          const m = line.material_id ? lib.materials[line.material_id] : undefined;
          const b = group.bom[i];
          const set = (p: Row) => save({ custom_bom: bom.map((l, k) => (k === i ? { ...l, ...p } : l)) });
          if (!line.material_id)
            return (
              <Ln
                key={i}
                left={
                  <>
                    <Edit disabled={!canEdit} value={line.label ?? ''} placeholder="what it is" onSave={(v) => set({ label: v || null })} className="cell" />
                    <div className="row mid" style={{ gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                      <Edit disabled={!canEdit} value={line.vendor ?? ''} placeholder="vendor" onSave={(v) => set({ vendor: v || null })} className="cell" />
                      <Edit disabled={!canEdit} type="date" value={line.quote_date ?? ''} onSave={(v) => set({ quote_date: v || null })} />
                      <Edit disabled={!canEdit} value={line.link ?? ''} placeholder="link" onSave={(v) => set({ link: v || null })} className="cell" />
                      {line.link && /^https?:\/\//.test(String(line.link)) && (
                        <a href={String(line.link)} target="_blank" rel="noreferrer">
                          <small>open</small>
                        </a>
                      )}
                    </div>
                    {b?.warnings.map((w) => (
                      <div key={w} className="warn">
                        {w}
                      </div>
                    ))}
                  </>
                }
                right={
                  <span className="nowrap">
                    <Edit className="short right" disabled={!canEdit} value={line.qty ?? ''} parse={(t) => (t.trim() === '' ? '' : readLength(t) ?? undefined)} onSave={(v) => set({ qty: v === '' ? 0 : v })} /> ×{' '}
                    <Edit className="short right" disabled={!canEdit} value={line.unit_cost ?? ''} placeholder="$ each" parse={(t) => (t.trim() === '' ? '' : Number.isFinite(Number(t.replace(/[$,]/g, ''))) ? Number(t.replace(/[$,]/g, '')).toFixed(2) : undefined)} onSave={(v) => set({ unit_cost: v === '' ? null : v })} />{' '}
                    → <Money cents={b?.cents} />{' '}
                    {canEdit && (
                      <Btn small title="Remove this line" onClick={() => window.confirm(`Remove "${line.label ?? 'this line'}" from this item's BOM?`) && void save({ custom_bom: bom.filter((_, k) => k !== i) })}>
                        ×
                      </Btn>
                    )}
                  </span>
                }
              />
            );
          return (
            <Ln
              key={i}
              left={
                <>
                  {m?.description ?? '(material removed)'}
                  <small>
                    {' '}
                    · {m ? priceWords(m) : 'no price'}
                  </small>
                  {b?.warnings.map((w) => (
                    <div key={w} className="warn">
                      {w}
                    </div>
                  ))}
                </>
              }
              right={
                <span className="nowrap">
                  <Edit
                    className="short right"
                    disabled={!canEdit}
                    value={line.qty ?? ''}
                    parse={(t) => (t.trim() === '' ? '' : readLength(t) ?? undefined)}
                    onSave={(v) => save({ custom_bom: bom.map((l, k) => (k === i ? { ...l, qty: v === '' ? 0 : v } : l)) })}
                  />{' '}
                  → <Money cents={b?.cents} />{' '}
                  {canEdit && (
                    <Btn small title="Remove this line" onClick={() => window.confirm(`Remove "${m?.description ?? 'this material'}" from this item's BOM?`) && void save({ custom_bom: bom.filter((_, k) => k !== i) })}>
                      ×
                    </Btn>
                  )}
                </span>
              }
            />
          );
        })}
        {canEdit && (
          <div className="row mid" style={{ marginTop: 8, gap: 6 }}>
            <MaterialPick lib={lib} value={mat} onPick={setMat} />
            <Btn
              small
              disabled={!mat}
              onClick={async () => {
                await save({ custom_bom: [...bom, { material_id: mat, qty: 1 }] });
                setMat('');
              }}
            >
              + Material
            </Btn>
            <Btn small onClick={() => void save({ custom_bom: [...bom, { material_id: null, label: null, qty: 1, unit_cost: null }] })}>
              + Typed line
            </Btn>
          </div>
        )}
      </Panel>
    </>
  );
}

// ---------- several items at once ----------

export function MultiEdit({ ctx, items }: { ctx: JobCtx; items: ResolvedItem[] }) {
  const lib = ctx.r.lib!;
  const canEdit = useSession().canEdit('screen.item') && !ctx.locked;
  const first = items[0]!;
  // a material picked per item, and an input hidden on any of them, are edited one item at a time
  const sharedInputs = first.inputs.filter((i) => i.data_type !== 'material' && items.every((it) => it.inputs.some((x) => x.key === i.key && x.data_type === i.data_type && !x.hidden)));
  const sharedStandards = first.standards_used.filter((st) => items.every((it) => it.standards_used.some((x) => x.key === st.key)));
  const numbers = items.map((i) => i.number).join(', ');
  const total = items.reduce((n, i) => n + i.price_cents, 0);

  const spread = (values: Scalar[], type: string, unit: string | null) => {
    const texts = values.map((v) => showValue(v, type, unit));
    return new Set(texts).size === 1 ? texts[0]! : texts.join(' · ');
  };

  return (
    <div className="row">
      <div className="col">
        <Panel label={`Shared inputs · a change here writes to ${numbers}`}>
          {sharedInputs.length === 0 && <Empty>These items share no inputs.</Empty>}
          {sharedInputs.map((inp) => {
            const all = items.map((it) => it.inputs.find((x) => x.key === inp.key)!);
            const lockedAny = all.some((x) => x.source === 'parent' || x.source === 'standard');
            const same = new Set(all.map((x) => String(x.value))).size === 1;
            return (
              <Ln
                key={inp.key}
                left={inp.label}
                right={
                  lockedAny ? (
                    <span className="lk">{spread(all.map((x) => x.value), inp.data_type, inp.unit)}</span>
                  ) : (
                    <span className="nowrap">
                      {!same && <small>{spread(all.map((x) => x.value), inp.data_type, inp.unit)} → </small>}
                      <ValueEditor
                        type={inp.data_type}
                        unit={inp.unit}
                        choices={inp.choices}
                        value={same ? inp.value : null}
                        disabled={!canEdit}
                        onSave={async (v) => {
                          for (const it of items) await writeItem(ctx, it, 'inputs', inp.key, v);
                        }}
                      />
                    </span>
                  )
                }
              />
            );
          })}
        </Panel>
      </div>
      <div className="col stack">
        {sharedStandards.length > 0 && (
          <Panel label="Shared job standards · tick to hold a value on these items">
            {sharedStandards.map((st) => {
              const def = lib.standards.find((d) => d.key === st.key)!;
              if (def.data_type === 'material') return null;
              const all = items.map((it) => it.standards_used.find((x) => x.key === st.key)!);
              const anyOver = all.some((x) => x.overridden);
              const unit = def.data_type === 'number' ? def.unit ?? null : null;
              return (
                <Ln
                  key={st.key}
                  left={
                    <label className="check">
                      <input
                        type="checkbox"
                        disabled={!canEdit}
                        checked={anyOver}
                        onChange={async (e) => {
                          for (const it of items) await writeItem(ctx, it, 'overrides', st.key, e.target.checked ? st.job_value : null);
                        }}
                      />
                      {st.label} (shared)
                    </label>
                  }
                  right={
                    anyOver ? (
                      <span className="nowrap">
                        <small>{showValue(st.job_value, def.data_type, unit)} → </small>
                        <span className="ov">
                          <ValueEditor
                            type={def.data_type}
                            unit={unit}
                            choices={def.choices?.map((c) => c.value) ?? null}
                            value={new Set(all.map((x) => String(x.value))).size === 1 ? all[0]!.value : null}
                            disabled={!canEdit}
                            onSave={async (v) => {
                              for (const it of items) await writeItem(ctx, it, 'overrides', st.key, v);
                            }}
                          />
                        </span>
                      </span>
                    ) : (
                      <span className="lk">{showValue(st.job_value, def.data_type, unit)}</span>
                    )
                  }
                />
              );
            })}
          </Panel>
        )}
        <Panel label={`${items.length} items selected`}>
          <span className="big">
            <Money cents={total} />
          </span>
          {items.map((i) => (
            <Ln key={i.id} left={`${i.number} · ${i.name}`} right={<Money cents={i.price_cents} />} />
          ))}
        </Panel>
      </div>
    </div>
  );
}
